// ============================================================================
// ClientAuthContext — the PORTAL's source of truth for "which client is signed
// in". A deliberately SEPARATE realm from the trainer AuthContext:
//   • backed by GET /api/client/me (vf_client_session cookie), not /api/auth/me
//   • its own query-key root (qk.client.*) so the two caches never collide
//   • a 401 resolves to null (logged-out is a normal state), never throws up
//   • claim / login / logout call the client API then refresh qk.client.me
// A trainer being signed in has zero effect here, and vice-versa, so a client
// can never reach trainer routes and a trainer can never reach portal routes.
// ============================================================================
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, clientApi, qk } from "../../api";
import type {
  ClientClaimPayload,
  ClientLoginPayload,
  PasswordResetPayload,
  PortalClient,
} from "../../api";

interface ClientAuthContextValue {
  client: PortalClient | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (payload: ClientLoginPayload) => Promise<PortalClient>;
  claim: (payload: ClientClaimPayload) => Promise<PortalClient>;
  /**
   * Wave 8 — consume a reset token + set a new password. The server signs the
   * client straight in (cookie); we prime `client.me` with the returned client
   * so the reset flows directly into the portal with no extra login.
   */
  reset: (payload: PasswordResetPayload) => Promise<PortalClient>;
  logout: () => Promise<void>;
}

const ClientAuthContext = createContext<ClientAuthContextValue | null>(null);

export function ClientAuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();

  const meQuery = useQuery<PortalClient | null>({
    queryKey: qk.client.me,
    queryFn: async ({ signal }) => {
      try {
        return await clientApi.me(signal);
      } catch (err) {
        if (err instanceof ApiError && err.isUnauthorized) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });

  const loginMutation = useMutation({
    mutationFn: (payload: ClientLoginPayload) => clientApi.login(payload),
    onSuccess: (res) => qc.setQueryData(qk.client.me, res.client),
  });

  const claimMutation = useMutation({
    mutationFn: (payload: ClientClaimPayload) => clientApi.claim(payload),
    onSuccess: (res) => qc.setQueryData(qk.client.me, res.client),
  });

  const login = useCallback(
    async (payload: ClientLoginPayload) => {
      const res = await loginMutation.mutateAsync(payload);
      return res.client;
    },
    [loginMutation],
  );

  const claim = useCallback(
    async (payload: ClientClaimPayload) => {
      const res = await claimMutation.mutateAsync(payload);
      return res.client;
    },
    [claimMutation],
  );

  const resetMutation = useMutation({
    mutationFn: (payload: PasswordResetPayload) => clientApi.reset(payload),
    onSuccess: (res) => qc.setQueryData(qk.client.me, res.client),
  });

  const reset = useCallback(
    async (payload: PasswordResetPayload) => {
      const res = await resetMutation.mutateAsync(payload);
      return res.client;
    },
    [resetMutation],
  );

  const logout = useCallback(async () => {
    await clientApi.logout();
    qc.setQueryData(qk.client.me, null);
    // Drop every client-realm cache entry so no prior client's data lingers.
    qc.removeQueries({ queryKey: ["client"] });
  }, [qc]);

  const value = useMemo<ClientAuthContextValue>(
    () => ({
      client: meQuery.data ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: !!meQuery.data,
      login,
      claim,
      reset,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, login, claim, reset, logout],
  );

  return (
    <ClientAuthContext.Provider value={value}>
      {children}
    </ClientAuthContext.Provider>
  );
}

export function useClientAuth(): ClientAuthContextValue {
  const ctx = useContext(ClientAuthContext);
  if (!ctx)
    throw new Error("useClientAuth must be used within <ClientAuthProvider>");
  return ctx;
}
